import _ from 'lodash'
import { DayOfWeek, ITimeRange } from 'types/datetime'
import {
  IRate,
  IDeliveryTimePreference,
  ICarrierCapacity,
  IPickupTimePreference,
  IOfferLimits,
  IBand,
} from 'apps/marketplace/store/contractLanes/types'
import {
  DELIVERY_TIME_LABELS,
  ANY_PICKUP_TIME,
  ANY,
  SAME,
  DIFFERENT,
} from 'apps/marketplace/utils/constants/rates'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'
import { EQUIPMENT } from 'constants/equipment'

export const locationsToLane = ({
  pickup: { city: pickupCity, stateCode: pickupState } = { city: '', stateCode: '' },
  delivery: { city: deliveryCity, stateCode: deliveryState } = { city: '', stateCode: '' },
}) => {
  const delivery = deliveryCity && deliveryState ? `${deliveryCity}, ${deliveryState}` : 'Anywhere'
  return `${pickupCity}, ${pickupState} – ${delivery}`
}

export const groupByEquipment = ({ lane, rates }: { rates: IRate[]; lane: string }) =>
  _(rates)
    .groupBy('equipment.name')
    .map((group, equipmentName) => {
      const equipmentOrder = _.findIndex(EQUIPMENT, { name: equipmentName })
      return {
        lane,
        equipmentOrder,
        equipmentName,
        equipmentId: EQUIPMENT[equipmentOrder].id,
        rates: _.orderBy(
          group,
          [
            'deliveryRadiusRangeMax',
            ({ approvedAt }) => !!approvedAt,
            ({ rate }) => parseFloat(rate),
          ],
          ['asc', 'desc', 'asc']
        ),
      }
    })
    .sortBy('equipmentOrder')
    .value()

export const groupRates = (contractLanes: IRate[]) =>
  _(contractLanes)
    .groupBy(locationsToLane)
    .map((rates, lane) => ({ lane, rates }))
    .orderBy(['rates.length', 'lane'], ['desc', 'asc'])
    .map(groupByEquipment)
    .flatten()
    .value()

export const linkRadiusRates = (rates: IRate[]) => {
  // link radius rates that were created same except for radius range and rate
  const bands = _.groupBy(
    rates,
    ({
      pickup: { city, stateCode },
      carrier: { id: carrierId },
      equipment: { name: equipmentName },
      isOnlyBeer,
      isOnlyDropTrailer,
      shipper: { id: shipperId } = { id: '' },
    }) =>
      [carrierId, city, stateCode, equipmentName, isOnlyBeer, isOnlyDropTrailer, shipperId].join(
        '-'
      )
  )
  _.forEach(bands, band => {
    band.sort(({ deliveryRadiusRangeMax: maxOne }, { deliveryRadiusRangeMax: maxTwo }) => {
      return maxOne && maxTwo ? maxOne - maxTwo : -1
    })
    band.forEach(radiusRate => {
      radiusRate.band = band
    })
  })
  return rates
}

export const filterRates = (contractLanes: IRate[]) => {
  const [laneRates, radiusRates] = _.partition(contractLanes, 'delivery')
  return {
    laneRates: groupRates(laneRates),
    radiusRates: groupRates(linkRadiusRates(radiusRates)),
  }
}

const summaryEntryFormat = (
  value: string | number,
  since: string,
  till: string,
  trucks = false
) => {
  let truckText = ''
  if (trucks) {
    truckText = Number(value) > 1 ? ' trucks' : ' truck'
  }
  return `${value}${truckText} ${capitalize(since, false, 3)}${
    since !== till ? ` – ${capitalize(till, false, 3)}` : ''
  }`
}

export const weekSummaryOptions = {
  isValid: (x: string | number) => Boolean(x),
  key: (x: string | number) => x,
  format: summaryEntryFormat,
}

export const weekSummary = (
  values: { [key: string]: string } = {},
  {
    isValid = weekSummaryOptions.isValid,
    key = weekSummaryOptions.key,
    format = weekSummaryOptions.format,
  } = weekSummaryOptions
) => {
  const sequences: string[] = []
  let last: number | string
  let since: string | undefined

  FORMATS.fullWeekList.forEach((dayOfWeek: string, i: number, days) => {
    const value = values[key(dayOfWeek)]
    since = since || dayOfWeek

    if (last && last !== value) {
      if (isValid(last)) {
        sequences.push(format(last, since, days[i - 1]))
      }
      since = dayOfWeek
    }

    if (i === days.length - 1 && isValid(value)) {
      sequences.push(format(value, since, dayOfWeek))
    }

    since = value ? since : undefined
    last = value
  })

  return sequences.join(', ')
}

export const isPositiveInteger = (number: string | number) =>
  _.isInteger(number) && Number(number) > 0

export const carrierCapacitySummary = (capacities = {}, trucks = false) =>
  weekSummary(capacities, {
    isValid: isPositiveInteger,
    key: dayOfWeek => `${dayOfWeek}Limit`,
    format: (value, since, till) => summaryEntryFormat(value, since, till, trucks),
  }) || 'Volume N/A'

export const pickupTimeEntryFormat = (value: string | number, since: string, till: string) =>
  `${capitalize(since, false, 3)}${
    since !== till ? ` – ${capitalize(till, false, 3)}` : ''
  } ${value}`

export interface IPickupTimeSummary {
  dayOfWeek: string
  startTime?: string | null
  endTime?: string | null
}

export const pickupTimeSummary = (pickupTimePreferences?: IPickupTimeSummary[]) => {
  if (!pickupTimePreferences || !pickupTimePreferences.length) return ANY_PICKUP_TIME

  const hash: { [key: string]: string } = {}
  pickupTimePreferences.forEach(({ dayOfWeek, endTime, startTime }) => {
    hash[dayOfWeek] = `${startTime} – ${endTime}`
  })

  return (
    weekSummary(hash, {
      isValid: value => value !== 'null – null',
      key: (x: string | number) => x,
      format: pickupTimeEntryFormat,
    }) || ANY_PICKUP_TIME
  )
}

export type DaysOfWeekLimit =
  | 'mondayLimit'
  | 'tuesdayLimit'
  | 'wednesdayLimit'
  | 'thursdayLimit'
  | 'fridayLimit'
  | 'saturdayLimit'
  | 'sundayLimit'

interface IGetCarrierCapacity {
  values: { [key: string]: string }
  pickupCity: string
  pickupStateCode: string
  pickupRadius: number
  alreadySet: boolean
  summary: string
}

export const carrierCapacityIsSet = (capacities?: IOfferLimits): boolean =>
  FORMATS.fullWeekList.some((dayOfWeek: string) => {
    const day = `${dayOfWeek}Limit` as DaysOfWeekLimit
    return capacities ? isPositiveInteger(capacities[day]) : false
  })

export const deliveryTimeSummary = (deliveryTimePreference: IDeliveryTimePreference) =>
  DELIVERY_TIME_LABELS[deliveryTimePreference] || DELIVERY_TIME_LABELS.any

export const getCarrierCapacity = (values?: IOfferLimits): IGetCarrierCapacity => {
  const alreadySet = carrierCapacityIsSet(values)
  return {
    values: FORMATS.fullWeekList.reduce((result, dayOfWeek) => {
      const key = `${dayOfWeek}Limit` as DaysOfWeekLimit
      return Object.assign(result, { [key]: values ? values[key] : '' })
    }, {}),
    pickupCity: _.get(values, 'pickupLocation.city') || values?.pickupCity || '',
    pickupStateCode: _.get(values, 'pickupLocation.stateCode') || values?.pickupStateCode || '',
    pickupRadius: Number(_.get(values, 'pickupLocation.radius') || values?.pickupRadius || 0),
    alreadySet,
    summary: alreadySet ? carrierCapacitySummary(values, true) : '',
  }
}

export const areLimitsValid = (limits = {}) => {
  const values = _.filter(limits, (value, key) => key.includes('Limit'))
  return _.some(values, isPositiveInteger) && _.every(values, value => !value || value > 0)
}

export const timeRangeValid = ({
  endTime,
  startTime,
}: {
  startTime?: string | null
  endTime?: string | null
}) => !!startTime === !!endTime

export const pickupDayChecked = (
  dayOfWeek: DayOfWeek,
  checked: boolean | undefined,
  carrierCapacity?: ICarrierCapacity
): boolean => {
  if (checked === undefined && carrierCapacity?.values) {
    const key = `${dayOfWeek}Limit` as DaysOfWeekLimit
    return !!carrierCapacity.values[key]
  }
  return !!checked
}

export const preparePickupTimePreferences = (
  type: string,
  different: IPickupTimePreference[],
  carrierCapacity?: ICarrierCapacity,
  everyday?: ITimeRange
): ITimeRange[] | undefined => {
  if (type === ANY) return undefined

  different.forEach(entry => {
    if (entry.startTime === entry.endTime) {
      entry.startTime = ''
      entry.endTime = ''
    }
  })

  const allSame = type === SAME
  return different.map(({ checked, dayOfWeek, endTime, startTime }) => {
    if (!pickupDayChecked(dayOfWeek, checked, carrierCapacity)) {
      return { dayOfWeek, startTime: '', endTime: '' }
    }
    return allSame ? { ...everyday, dayOfWeek } : { dayOfWeek, startTime, endTime }
  })
}

export const getPickupTimePreferences = (pickupTimePreferences: IPickupTimePreference[] = []) => {
  let hasValue = false
  let allSame = true
  let startTime: string = ''
  let endTime: string = ''
  pickupTimePreferences.forEach(preference => {
    if (preference.startTime && preference.endTime) {
      preference.checked = true
      hasValue = true
      startTime = startTime || preference.startTime
      endTime = endTime || preference.endTime
      allSame = allSame && startTime === preference.startTime && endTime === preference.endTime
    } else {
      // when undefined, default is taken from  carrierCapacity.
      preference.checked = false
    }
  })

  if (!hasValue) {
    return { pickupTimeType: ANY }
  }
  return allSame
    ? { pickupTimeType: SAME, pickupTimeEveryday: { startTime, endTime }, pickupTimePreferences }
    : { pickupTimeType: DIFFERENT, pickupTimePreferences }
}

export const getRadiusRates = (rates: IBand[], band: IRate[]) => {
  return !band && rates
    ? rates
    : rates.map(({ max, min, value }) => {
        const { contractId: id, rate } =
          band.find(
            (x: { deliveryRadiusRangeMin: number | null; deliveryRadiusRangeMax: number | null }) =>
              x.deliveryRadiusRangeMin === min && x.deliveryRadiusRangeMax === max
          ) || {}
        return { id, min, max, checked: !!id, value: rate || value }
      })
}

export const getLocation = (place?: { city: string; stateCode: string }) => {
  return place
    ? { city: place.city, state: place.stateCode, stateCode: place.stateCode }
    : undefined
}
